export interface IRgbColor {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

export function parseHexColor(color: string): IRgbColor | null {
  const hex = color.trim();
  if (!/^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(hex)) return null;

  const normalized = hex.startsWith("#") ? hex.slice(1) : hex;
  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((ch) => ch + ch)
          .join("")
      : normalized;

  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  return { r, g, b };
}

export function getContrastingTextColor(backgroundColor: string): string {
  const rgb = parseHexColor(backgroundColor);
  if (rgb === null) return "#ffffff";

  // perceived luminance 0..255
  const luminance = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
  return luminance > 160 ? "#111827" : "#ffffff";
}

export function getTagChipStyle(backgroundColor?: string): Record<string, string> {
  if (typeof backgroundColor !== "string" || backgroundColor.trim().length === 0) {
    return {};
  }

  return {
    backgroundColor,
    borderColor: backgroundColor,
    color: getContrastingTextColor(backgroundColor),
  };
}

export function formatRowCount(count: number): string {
  if (count < 1000) return String(count);
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K`;
  return `${(count / 1000000).toFixed(2)}M`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Есть ли в заметке видимое содержимое.
 *
 * Норма: spec/backend/source-explorer/notes.md R01 — текст заметки Markdown;
 * R09 — в превью-списки попадают объекты с непустым текстом после отсечения
 * пробельных символов.
 */
export function hasNoteContent(noteText: string | undefined): boolean {
  return (noteText ?? "").trim().length > 0;
}

/**
 * Заметка как обычный текст: разметка markdown снимается.
 *
 * Нужна там, где показывать разметку нельзя, — подсказки, ячейки списков,
 * заголовки. Адреса картинок в текст не попадают: встроенная картинка может
 * нести мегабайты данных, и в превью им делать нечего.
 */
export function noteToPlainText(noteText: string | undefined): string {
  const raw = (noteText ?? "").trim();
  if (raw.length === 0) return "";

  const withoutFences = raw.replace(/^[ \t]*(?:```|~~~).*$/gm, " ");
  const withoutImages = withoutFences.replace(
    /!\[([^\]]*)\]\((?:<[^>]*>|[^)]*)\)/g,
    "$1",
  );
  const withoutLinks = withoutImages.replace(
    /\[([^\]]*)\]\((?:<[^>]*>|[^)]*)\)/g,
    "$1",
  );
  const withoutBlockMarks = withoutLinks
    .replace(/^[ \t]*>+[ \t]?/gm, " ")
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, " ")
    .replace(/^[ \t]*(?:[-*+]|\d+[.)])[ \t]+/gm, " ")
    .replace(/^[ \t]*(?:-{3,}|\*{3,}|_{3,})[ \t]*$/gm, " ");
  const withoutInlineMarks = withoutBlockMarks
    .replace(/(\*\*|__|~~)/g, "")
    .replace(/`+/g, "")
    .replace(/(^|[^\w])[*_]([^*_]+)[*_]($|[^\w])/g, "$1$2$3");
  const unescaped = withoutInlineMarks.replace(/\\([\\`*_[\]<>#+\-.!|=)])/g, "$1");

  return unescaped.replace(/\s+/g, " ").trim();
}

/** Начало заметки обычным текстом — для списков и подсказок. */
export function getNotePreview(
  noteText: string | undefined,
  maxLength: number = 140,
): string {
  const text = noteToPlainText(noteText);
  if (text.length <= maxLength) return text;
  if (maxLength <= 3) return text.slice(0, maxLength);
  return `${text.slice(0, maxLength - 3).trimEnd()}...`;
}
